import {useLocaleKeys} from '../../../LocaleKeysProvider';
import {LocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {Maybe, Price} from '@wix/wixstores-graphql-schema-node';
import {useControllerProps} from '../../ControllerContext';

type TaxLine = {title: string; value: string};

export function useTaxBreakdownLines() {
  const localeKeys = useLocaleKeys();
  const {taxInfo} = useControllerProps().thankYouPageStore;

  let lines: TaxLine[] = [];

  if (taxInfo?.taxBreakdown?.some((item) => item?.taxName)) {
    lines = taxInfo.taxBreakdown.reduce<TaxLine[]>((result, item) => {
      /* istanbul ignore else */
      if (item.aggregatedTaxAmount?.amount) {
        return result.concat(
          buildTaxLines({
            taxName: item.taxName,
            taxAmount: item.aggregatedTaxAmount,
            exemptAmount: item.aggregatedExemptAmount,
            localeKeys,
          })
        );
      }
      /* istanbul ignore next */
      return result;
    }, []);
  } else if (taxInfo?.totalTax?.amount) {
    lines = buildTaxLines({
      taxAmount: taxInfo.totalTax,
      exemptAmount: taxInfo.totalExempt,
      localeKeys,
    });
  }

  return lines;
}

function buildTaxLines({
  taxAmount,
  exemptAmount,
  taxName,
  localeKeys,
}: {
  taxAmount: Maybe<Price>;
  exemptAmount: Maybe<Price>;
  taxName?: Maybe<string>;
  localeKeys: LocaleKeys;
}): TaxLine[] {
  const hasExemption = parseFloat(exemptAmount?.amount ?? /* istanbul ignore next */ '0') > 0;
  const isPartialExemption = parseFloat(taxAmount?.amount ?? /* istanbul ignore next */ '0') > 0;

  const title: string = taxName ?? localeKeys.thankYouPage.tax.label();

  if (!hasExemption) {
    return [{title, value: taxAmount?.formattedAmount ?? /* istanbul ignore next */ ''}];
  }

  if (!isPartialExemption) {
    return [
      {
        title: localeKeys.thankYouPage.taxExemption.full.label({name: title}) as string,
        value: taxAmount?.formattedAmount ?? /* istanbul ignore next */ '',
      },
    ];
  }

  return [
    {title, value: taxAmount?.formattedAmount ?? /* istanbul ignore next */ ''},
    {
      title: localeKeys.thankYouPage.taxExemption.partial.label(),
      value: `-${exemptAmount?.formattedAmount ?? /* istanbul ignore next */ ''}`,
    },
  ];
}
